import { useMutation } from '@apollo/react-hooks';
import { Field, FormikProvider, useFormik } from 'formik';
import Head from 'next/head';
import Link from 'next/link';
import React, { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import styled from 'styled-components';
import { Box, CenterCol as Center, FlexGrow, Row } from '../../components/Flex';
import { BackgroundButton, EditableInput } from '../../components/Form';
import GraphQLErrorText from '../../components/GraphQLErrorText';
import { Body, HeadingOne } from '../../components/Text';
import { COOKIE, LOCAL_STORAGE } from '../../constants';
import { REGISTER } from '../../lib/graphql/mutations';
import { WHITE_LABEL_MARKET } from '../../lib/white-label';
import { COLORS } from '../../theme';
import * as Yup from 'yup';
import { analytics } from '../../lib/analytics';

const Container = styled(Row)`
  width: 100%;
  margin-right: auto;
  margin-left: auto;
  height: 100vh;
`;

const Left = styled(Center)`
  width: 90%;
  min-width: 450px;
  max-width: 700px;
  background: linear-gradient(190deg, #0688f3, #1cc9cb);
  @media only screen and (max-width: 850px) {
    display: none;
  }
`;

const Right = styled(Center)`
  width: 100%;
`;

const Title = styled(HeadingOne)`
  text-align: center;
`;

const Inputs = styled.form`
  width: 90%;
  max-width: 500px;
  margin: 0 auto;
`;

const A = styled.a`
  color: ${COLORS.TEAL_BLUE};
  cursor: pointer;
  text-align: right;
  margin: 0;
  &:hover {
    text-decoration: underline;
  }
`;

const Text = styled(Body)`
  font-size: 1rem;
  text-align: right;
  margin: 0;
`;

const RegisterSchema = Yup.object().shape({
  username: Yup.string()
    .matches(new RegExp(/^[\w-]+$/g), 'Must be alphanumeric')
    .max(15, 'Cannot be more than 15 characters')
    .required('Required'),
  firstName: Yup.string().required('Required'),
  lastName: Yup.string().required('Required'),
  email: Yup.string().email('Must be a valid email').required('Required'),
  password: Yup.string()
    .min(8, 'Must be longer then 8 characters')
    .required('Required'),
});

function SignUp() {
  const [cookies, setCookie] = useCookies([
    COOKIE.ACCESS_TOKEN,
    COOKIE.REFRESH_TOKEN,
  ]);
  const [passwordType, setPasswordType] = useState(true);
  const [register, { loading, error, called }] = useMutation(REGISTER);
  useEffect(() => {
    analytics.page(`Register`);
  }, []);

  const formik = useFormik({
    initialValues: {
      username: '',
      firstName: '',
      lastName: '',
      email: '',
      password: '',
    },
    validationSchema: RegisterSchema,
    onSubmit: (values) => {
      register({
        variables: {
          registerData: values,
        },
      })
        .then((res) => {
          if (res.data.register) {
            analytics.identify(res?.data?.register?.id, {
              email: values.email,
              firstName: values.firstName,
              lastName: values.lastName,
              username: values.username,
            });
            analytics.track('User Signed Up');

            localStorage.removeItem(LOCAL_STORAGE.REDIRECT_AFTER_AUTH);
            setCookie(COOKIE.ACCESS_TOKEN, res.data.register.accessToken, {
              expires: new Date(2147483647000),
              path: '/',
            });
            setCookie(COOKIE.REFRESH_TOKEN, res.data.register.refreshToken, {
              expires: new Date(2147483647000),
              path: '/',
            });
            window.location.assign('/register/verify-email');
          }
        })
        .catch((err) => {
          // Add error tracking here @TODO
        });
    },
  });

  return (
    <Container>
      <Head>
        <title>Register - {WHITE_LABEL_MARKET}</title>
      </Head>
      <Left>
        <img alt="" src="/images/bot-ui@2x.png" />
      </Left>
      <Right>
        <Box height={40} />
        <Title>Create an Account</Title>
        <Box height={10} />
        <Text>
          Already have an account?&nbsp;
          <Link href="/login" legacyBehavior>
            <A>Log In</A>
          </Link>
        </Text>
        <Box height={40} />
        <FormikProvider value={formik}>
          <Inputs onSubmit={formik.handleSubmit}>
            <Field
              name="username"
              placeholder="Username"
              type="text"
              component={EditableInput}
            />
            <Box height={20} />
            <Row>
              <Field
                name="firstName"
                placeholder="First Name"
                type="text"
                component={EditableInput}
              />
              <Box width={20} />
              <Field
                name="lastName"
                placeholder="Last Name"
                type="text"
                component={EditableInput}
              />
            </Row>
            <Box height={20} />
            <Field
              name="email"
              placeholder="Email Address"
              type="text"
              component={EditableInput}
            />
            <Box height={20} />
            <Field
              name="password"
              placeholder="Password"
              type={passwordType ? 'password' : 'text'}
              icon="/images/eye-close-up@2x.png"
              onIconClick={() => setPasswordType(!passwordType)}
              component={EditableInput}
            />
            <Box height={10} />
            {error ? <GraphQLErrorText error={error} /> : null}
            <Box height={20} />
            <Row>
              <FlexGrow />
              <BackgroundButton
                type="submit"
                disabled={loading || !formik.isValid}
              >
                Sign Up
              </BackgroundButton>
            </Row>
          </Inputs>
        </FormikProvider>
      </Right>
    </Container>
  );
}

export async function getStaticProps() {
  return {
    props: {
      defaultSpacing: false,
    },
  };
}

export default SignUp;
